import { IEnhancedNetworkRequest, IEnhancedNetworkResponse } from '../network-interceptor/types';
import { generateUniqueId } from '../utils';

export interface IStateChange {
  id: string;
  timestamp: number;
  statePath: string;
  oldValue: unknown;
  newValue: unknown;
  source: 'api' | 'user' | 'system' | 'unknown';
  trigger?: {
    type: 'api_request' | 'api_response' | 'user_action' | 'timer' | 'unknown';
    id?: string;
    details?: Record<string, unknown>;
  };
}

export interface IAPIStateMapping {
  id: string;
  apiRequestId: string;
  apiResponseId?: string;
  url: string;
  method: string;
  stateChanges: IStateChange[];
  timing: {
    apiStart: number;
    apiEnd?: number;
    firstStateChange?: number;
    lastStateChange?: number;
    totalDuration?: number;
  };
  confidence: number; // 0-1, API와 상태 변경 간의 연관성 신뢰도
  metadata: {
    dataFlowType: 'direct' | 'indirect' | 'batched' | 'unknown';
    affectedComponents: string[];
    stateChangeCount: number;
  };
}

export interface IAPIStateMapperConfig {
  enableAutoMapping?: boolean;
  stateChangeWindow?: number; // API 호출 후 상태 변경을 감지할 시간 창 (ms)
  confidenceThreshold?: number;
  maxMappingHistory?: number;
  enablePatternLearning?: boolean;
  excludeStatePatterns?: RegExp[];
  onMappingCreated?: (mapping: IAPIStateMapping) => void;
  onStateChange?: (stateChange: IStateChange) => void;
}

/**
 * API-상태 매핑 시스템
 * 
 * API 호출과 상태 변경 간의 연관성을 자동으로 분석하고 추적합니다.
 */
export class APIStateMapper {
  private _config: Required<Omit<IAPIStateMapperConfig, 'onMappingCreated' | 'onStateChange'>> & {
    onMappingCreated?: (mapping: IAPIStateMapping) => void;
    onStateChange?: (stateChange: IStateChange) => void;
  };
  
  private _isActive: boolean = false;
  private _pendingRequests = new Map<string, IEnhancedNetworkRequest>();
  private _recentStateChanges: IStateChange[] = [];
  private _mappings: IAPIStateMapping[] = [];
  private _stateObservers = new Map<string, any>();
  private _patterns = new Map<string, number>(); // URL 패턴 -> 신뢰도 점수

  constructor(config: IAPIStateMapperConfig = {}) {
    this._config = {
      enableAutoMapping: true,
      stateChangeWindow: 5000, // 5초
      confidenceThreshold: 0.6,
      maxMappingHistory: 1000,
      enablePatternLearning: true,
      excludeStatePatterns: [
        /^debug/,
        /^devtools/,
        /^react-devtools/,
        /^__/,
        /timestamp$/,
        /lastUpdated$/,
      ],
      ...config,
    };
  }

  /**
   * 매핑 시작
   */
  public start(): void {
    if (this._isActive) {
      console.warn('[APIStateMapper] Already active');
      return;
    }

    console.log('[APIStateMapper] Starting API-state mapping...');
    
    this._isActive = true;
    
    // 상태 관리 라이브러리들 감지 및 후킹
    this._setupStateObservers();
    
    console.log('[APIStateMapper] API-state mapping started');
  }

  /**
   * 매핑 중지
   */
  public stop(): void {
    if (!this._isActive) {
      return;
    }

    console.log('[APIStateMapper] Stopping API-state mapping...');
    
    this._isActive = false;
    
    // 상태 관찰자들 정리
    this._cleanupStateObservers();
    
    console.log('[APIStateMapper] API-state mapping stopped');
  }

  /**
   * API 요청 추적 시작
   */
  public trackAPIRequest(request: IEnhancedNetworkRequest): void {
    if (!this._isActive || !this._config.enableAutoMapping) {
      return;
    }

    this._pendingRequests.set(request.id, request);
    
    console.log(`[APIStateMapper] Tracking API request: ${request.method} ${request.url}`);
  }

  /**
   * API 응답 추적
   */
  public trackAPIResponse(response: IEnhancedNetworkResponse): void {
    if (!this._isActive || !this._config.enableAutoMapping) {
      return;
    }

    const request = this._pendingRequests.get(response.requestId);
    if (!request) {
      return;
    }

    console.log(`[APIStateMapper] Tracking API response: ${response.status} for ${request.url}`);

    // 상태 변경 감지 창 시작
    this._startStateChangeWindow(request, response);
  }

  /**
   * 상태 변경 수동 추가
   */
  public addStateChange(stateChange: Omit<IStateChange, 'id' | 'timestamp'>): void {
    const change: IStateChange = {
      id: generateUniqueId(),
      timestamp: Date.now(),
      ...stateChange,
    };

    this._recentStateChanges.push(change);

    // 최근 상태 변경 목록 크기 제한
    if (this._recentStateChanges.length > 100) {
      this._recentStateChanges = this._recentStateChanges.slice(-100);
    }

    // 콜백 호출
    if (this._config.onStateChange) {
      this._config.onStateChange(change);
    }

    // 자동 매핑 시도
    if (this._config.enableAutoMapping) {
      this._attemptAutoMapping(change);
    }
  }

  /**
   * 매핑 목록 가져오기
   */
  public getMappings(): IAPIStateMapping[] {
    return [...this._mappings];
  }

  /**
   * 특정 API 패턴의 매핑 가져오기
   */
  public getMappingsByURL(urlPattern: string | RegExp): IAPIStateMapping[] {
    const pattern = typeof urlPattern === 'string' ? new RegExp(urlPattern) : urlPattern;
    return this._mappings.filter(mapping => pattern.test(mapping.url));
  }

  /**
   * 상태 변경 히스토리
   */
  public getStateChangeHistory(): IStateChange[] {
    return [...this._recentStateChanges];
  }

  /**
   * 패턴 학습 결과
   */
  public getLearnedPatterns(): Map<string, number> {
    return new Map(this._patterns);
  }

  /**
   * 통계 정보
   */
  public getStats() {
    return {
      isActive: this._isActive,
      totalMappings: this._mappings.length,
      pendingRequests: this._pendingRequests.size,
      recentStateChanges: this._recentStateChanges.length,
      learnedPatterns: this._patterns.size,
      averageConfidence: this._mappings.length > 0 ? 
        this._mappings.reduce((sum, m) => sum + m.confidence, 0) / this._mappings.length : 0,
      config: { ...this._config },
    };
  }

  /**
   * 상태 변경 감지 창 시작
   */
  private _startStateChangeWindow(request: IEnhancedNetworkRequest, response: IEnhancedNetworkResponse): void {
    const windowStart = Date.now();
    const stateChangesAtStart = this._recentStateChanges.length;

    setTimeout(() => {
      const stateChangesAfter = this._recentStateChanges.slice(stateChangesAtStart);
      const relevantChanges = stateChangesAfter.filter(change => 
        change.timestamp >= windowStart && 
        change.timestamp <= windowStart + this._config.stateChangeWindow
      );

      if (relevantChanges.length > 0) {
        this._createMapping(request, response, relevantChanges);
      }

      // 처리된 요청 제거
      this._pendingRequests.delete(request.id);
    }, this._config.stateChangeWindow);
  }

  /**
   * API-상태 매핑 생성
   */
  private _createMapping(
    request: IEnhancedNetworkRequest, 
    response: IEnhancedNetworkResponse, 
    stateChanges: IStateChange[]
  ): void {
    const confidence = this._calculateConfidence(request, response, stateChanges);
    
    if (confidence < this._config.confidenceThreshold) {
      return;
    }

    const mapping: IAPIStateMapping = {
      id: generateUniqueId(),
      apiRequestId: request.id,
      apiResponseId: response.id,
      url: request.url,
      method: request.method,
      stateChanges,
      timing: {
        apiStart: request.timing.startTime,
        apiEnd: request.timing.responseEnd,
        firstStateChange: Math.min(...stateChanges.map(c => c.timestamp)),
        lastStateChange: Math.max(...stateChanges.map(c => c.timestamp)),
        totalDuration: request.timing.duration,
      },
      confidence,
      metadata: {
        dataFlowType: this._analyzeDataFlowType(request, response, stateChanges),
        affectedComponents: this._extractAffectedComponents(stateChanges),
        stateChangeCount: stateChanges.length,
      },
    };

    this._mappings.push(mapping);

    // 히스토리 크기 제한
    if (this._mappings.length > this._config.maxMappingHistory) {
      this._mappings = this._mappings.slice(-this._config.maxMappingHistory);
    }

    // 패턴 학습
    if (this._config.enablePatternLearning) {
      this._learnPattern(mapping);
    }

    console.log(`[APIStateMapper] Created mapping: ${request.method} ${request.url} -> ${stateChanges.length} state changes (confidence: ${confidence.toFixed(2)})`);

    // 콜백 호출
    if (this._config.onMappingCreated) {
      this._config.onMappingCreated(mapping);
    }
  }

  /**
   * 신뢰도 계산
   */
  private _calculateConfidence(
    request: IEnhancedNetworkRequest, 
    response: IEnhancedNetworkResponse, 
    stateChanges: IStateChange[]
  ): number {
    let confidence = 0.5; // 기본 신뢰도

    // 타이밍 기반 신뢰도
    const apiDuration = request.timing.duration;
    const stateChangeDelay = stateChanges.length > 0 ? 
      Math.min(...stateChanges.map(c => c.timestamp)) - (request.timing.startTime + apiDuration) : 0;
    
    if (stateChangeDelay < 100) {
      confidence += 0.3; // 즉시 상태 변경
    } else if (stateChangeDelay < 500) {
      confidence += 0.2; // 빠른 상태 변경
    } else if (stateChangeDelay < 1000) {
      confidence += 0.1; // 적당한 상태 변경
    }

    // 응답 상태 기반 신뢰도
    if (response.status >= 200 && response.status < 300) {
      confidence += 0.1; // 성공적인 응답
    } else {
      confidence -= 0.2; // 실패한 응답
    }

    // 상태 변경 수량 기반 신뢰도
    if (stateChanges.length > 0) {
      confidence += Math.min(stateChanges.length * 0.1, 0.2);
    }

    // 학습된 패턴 기반 신뢰도
    const urlPattern = this._extractURLPattern(request.url);
    const learnedConfidence = this._patterns.get(urlPattern);
    if (learnedConfidence) {
      confidence = (confidence + learnedConfidence) / 2;
    }

    // GraphQL 특별 처리
    if (request.graphql) {
      confidence += 0.1;
    }

    return Math.min(Math.max(confidence, 0), 1);
  }

  /**
   * 데이터 플로우 타입 분석
   */
  private _analyzeDataFlowType(
    request: IEnhancedNetworkRequest, 
    response: IEnhancedNetworkResponse, 
    stateChanges: IStateChange[]
  ): 'direct' | 'indirect' | 'batched' | 'unknown' {
    if (stateChanges.length === 0) {
      return 'unknown';
    }

    const stateChangeDelay = Math.min(...stateChanges.map(c => c.timestamp)) - 
      (request.timing.startTime + request.timing.duration);

    // 직접적인 데이터 플로우
    if (stateChangeDelay < 100 && stateChanges.length <= 3) {
      return 'direct';
    }

    // 배치 처리
    if (stateChanges.length > 5) {
      return 'batched';
    }

    // 간접적인 데이터 플로우
    if (stateChangeDelay > 500) {
      return 'indirect';
    }

    return 'unknown';
  }

  /**
   * 영향받은 컴포넌트 추출
   */
  private _extractAffectedComponents(stateChanges: IStateChange[]): string[] {
    const components = new Set<string>();
    
    stateChanges.forEach(change => {
      const pathParts = change.statePath.split('.');
      if (pathParts.length > 0) {
        // 첫 번째 부분을 컴포넌트로 간주
        components.add(pathParts[0]);
      }
    });

    return Array.from(components);
  }

  /**
   * URL 패턴 추출
   */
  private _extractURLPattern(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.replace(/\/\d+/g, '/:id'); // ID를 일반화
    } catch (error) {
      return url;
    }
  }

  /**
   * 패턴 학습
   */
  private _learnPattern(mapping: IAPIStateMapping): void {
    const urlPattern = this._extractURLPattern(mapping.url);
    const currentConfidence = this._patterns.get(urlPattern) || 0.5;
    
    // 신뢰도 업데이트 (지수 이동 평균)
    const alpha = 0.3;
    const newConfidence = alpha * mapping.confidence + (1 - alpha) * currentConfidence;
    
    this._patterns.set(urlPattern, newConfidence);
  }

  /**
   * 자동 매핑 시도
   */
  private _attemptAutoMapping(stateChange: IStateChange): void {
    // 최근 완료된 API 요청들과 연관성 확인
    const recentTime = Date.now() - this._config.stateChangeWindow;
    
    // 이미 매핑된 요청들은 제외하고 확인
    // 여기서는 간단한 구현으로 생략
  }

  /**
   * 상태 관찰자 설정
   */
  private _setupStateObservers(): void {
    // Redux 상태 감지
    this._setupReduxObserver();
    
    // MobX 상태 감지
    this._setupMobXObserver();
    
    // Zustand 상태 감지
    this._setupZustandObserver();
    
    // React useState 감지 (어려움 - 기본 구현 생략)
    // Context API 감지 (어려움 - 기본 구현 생략)
  }

  /**
   * Redux 상태 관찰자 설정
   */
  private _setupReduxObserver(): void {
    if (typeof window === 'undefined') return;

    // Redux DevTools Extension 활용
    const reduxDevTools = (window as any).__REDUX_DEVTOOLS_EXTENSION__;
    if (reduxDevTools) {
      // Redux DevTools가 있으면 활용
      // 실제 구현에서는 Redux store에 직접 접근하는 것이 더 좋음
    }

    // 전역 Redux store 감지 시도
    if ((window as any).__REDUX_STORE__) {
      const store = (window as any).__REDUX_STORE__;
      const originalDispatch = store.dispatch;
      
      store.dispatch = (action: any) => {
        const prevState = store.getState();
        const result = originalDispatch(action);
        const nextState = store.getState();
        
        // 상태 변경 감지
        this._detectStateChanges('redux', prevState, nextState, action);
        
        return result;
      };
    }
  }

  /**
   * MobX 상태 관찰자 설정
   */
  private _setupMobXObserver(): void {
    // MobX는 observe 함수를 통해 상태 변경을 감지할 수 있음
    // 실제 구현에서는 MobX store들을 직접 관찰해야 함
    if (typeof window !== 'undefined' && (window as any).mobx) {
      // MobX 전역 관찰자 설정
      console.log('[APIStateMapper] MobX detected, setting up observer');
    }
  }

  /**
   * Zustand 상태 관찰자 설정
   */
  private _setupZustandObserver(): void {
    // Zustand는 subscribeWithSelector를 통해 상태 변경을 감지할 수 있음
    // 실제 구현에서는 Zustand store들을 직접 구독해야 함
    if (typeof window !== 'undefined' && (window as any).zustand) {
      console.log('[APIStateMapper] Zustand detected, setting up observer');
    }
  }

  /**
   * 상태 변경 감지
   */
  private _detectStateChanges(
    source: string, 
    prevState: any, 
    nextState: any, 
    trigger?: any
  ): void {
    const changes = this._diffStates('', prevState, nextState);
    
    changes.forEach(change => {
      if (this._shouldExcludeStateChange(change.statePath)) {
        return;
      }

      const stateChange: IStateChange = {
        id: generateUniqueId(),
        timestamp: Date.now(),
        statePath: change.statePath,
        oldValue: change.oldValue,
        newValue: change.newValue,
        source: 'api', // 추후 더 정확한 소스 감지
        trigger: trigger ? {
          type: 'unknown',
          details: trigger,
        } : undefined,
      };

      this.addStateChange(stateChange);
    });
  }

  /**
   * 상태 diff 계산
   */
  private _diffStates(
    basePath: string, 
    oldState: any, 
    newState: any
  ): Array<{ statePath: string; oldValue: any; newValue: any }> {
    const changes: Array<{ statePath: string; oldValue: any; newValue: any }> = [];

    if (oldState === newState) {
      return changes;
    }

    if (typeof oldState !== 'object' || typeof newState !== 'object' || 
        oldState === null || newState === null) {
      changes.push({
        statePath: basePath,
        oldValue: oldState,
        newValue: newState,
      });
      return changes;
    }

    // 객체/배열의 키들 확인
    const allKeys = new Set([...Object.keys(oldState), ...Object.keys(newState)]);
    
    for (const key of allKeys) {
      const newPath = basePath ? `${basePath}.${key}` : key;
      const oldValue = oldState[key];
      const newValue = newState[key];

      if (!(key in oldState)) {
        // 새로 추가된 키
        changes.push({
          statePath: newPath,
          oldValue: undefined,
          newValue: newValue,
        });
      } else if (!(key in newState)) {
        // 삭제된 키
        changes.push({
          statePath: newPath,
          oldValue: oldValue,
          newValue: undefined,
        });
      } else if (oldValue !== newValue) {
        // 변경된 키
        if (typeof oldValue === 'object' && typeof newValue === 'object' && 
            oldValue !== null && newValue !== null) {
          // 재귀적으로 확인
          changes.push(...this._diffStates(newPath, oldValue, newValue));
        } else {
          changes.push({
            statePath: newPath,
            oldValue: oldValue,
            newValue: newValue,
          });
        }
      }
    }

    return changes;
  }

  /**
   * 상태 변경 제외 여부 확인
   */
  private _shouldExcludeStateChange(statePath: string): boolean {
    return this._config.excludeStatePatterns.some(pattern => pattern.test(statePath));
  }

  /**
   * 상태 관찰자들 정리
   */
  private _cleanupStateObservers(): void {
    this._stateObservers.clear();
  }

  /**
   * 정리
   */
  public destroy(): void {
    this.stop();
    this._pendingRequests.clear();
    this._recentStateChanges = [];
    this._mappings = [];
    this._patterns.clear();
  }
}

// 전역 인스턴스
let globalStateMapper: APIStateMapper | null = null;

/**
 * 전역 API 상태 매퍼 가져오기/생성
 */
export function getGlobalAPIStateMapper(config?: IAPIStateMapperConfig): APIStateMapper {
  if (!globalStateMapper) {
    globalStateMapper = new APIStateMapper(config);
  }
  return globalStateMapper;
}

/**
 * 전역 API 상태 매퍼 정리
 */
export function destroyGlobalAPIStateMapper(): void {
  if (globalStateMapper) {
    globalStateMapper.destroy();
    globalStateMapper = null;
  }
}
